// src/pages/teacher/TeacherOnlineClasses.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Row, Col, Card, Button, Badge, Spinner,
  Form, InputGroup
} from "react-bootstrap";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  getDoc, onSnapshot, serverTimestamp, query,
  orderBy, where, getDocs, limit
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { useAuth } from "../../context/AuthContext";

/* ------------ helpers ------------ */
const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
const fmt = (d, f = "DD MMM YYYY, h:mm A") =>
  d && dayjs(d).isValid() ? dayjs(d).format(f) : "—";
const lower = (s) => String(s || "").trim().toLowerCase();

const isLive = (row) => {
  const now = dayjs();
  const s = row.startAt ? dayjs(row.startAt) : null;
  const e = row.endAt ? dayjs(row.endAt) : null;
  if (s && e) return now.isAfter(s) && now.isBefore(e);
  if (s && !e) return now.isAfter(s) && now.isBefore(s.add(120, "minute")); // safety
  return false;
};
const isUpcoming = (row) => {
  const s = row.startAt ? dayjs(row.startAt) : null;
  return !!(s && s.isAfter(dayjs()));
};
const isEnded = (row) => !isLive(row) && !isUpcoming(row);

/* ------------ New Class Modal (inline & lightweight) ------------ */
function UpsertModal({ show, onClose, initial, onSave, context }) {
  const [form, setForm] = useState({
    title: "",
    subject: "",
    startAt: "",
    endAt: "",
    durationMin: "",
    platform: "meet",
    meetingURL: "",
    notes: "",
    status: "active",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    if (initial) {
      setForm({
        title: initial.title || "",
        subject: initial.subject || "",
        startAt: initial.startAt ? dayjs(toDate(initial.startAt)).format("YYYY-MM-DDTHH:mm") : "",
        endAt: initial.endAt ? dayjs(toDate(initial.endAt)).format("YYYY-MM-DDTHH:mm") : "",
        durationMin: initial.durationMin || "",
        platform: initial.platform || "meet",
        meetingURL: initial.meetingURL || "",
        notes: initial.notes || "",
        status: initial.status || "active",
      });
    } else {
      setForm({
        title: "",
        subject: "",
        startAt: "",
        endAt: "",
        durationMin: "",
        platform: "meet",
        meetingURL: "",
        notes: "",
        status: "active",
      });
    }
  }, [show, initial]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) return Swal.fire({ icon: "warning", title: "Title is required" });
    if (!form.startAt) return Swal.fire({ icon: "warning", title: "Start time is required" });
    if (form.meetingURL && !/^https?:\/\//i.test(form.meetingURL.trim())) {
      return Swal.fire({ icon: "info", title: "Add http:// or https:// to meeting link" });
    }

    setSaving(true);
    try {
      await onSave({
        title: form.title.trim(),
        subject: form.subject.trim(),
        startAt: form.startAt ? new Date(form.startAt) : null,
        endAt: form.endAt ? new Date(form.endAt) : null,
        durationMin: form.durationMin ? Number(form.durationMin) : null,
        platform: form.platform,
        meetingURL: form.meetingURL.trim(),
        notes: form.notes.trim(),
        status: form.status || "active",
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;
  return (
    <div className="glassy-backdrop" onClick={onClose}>
      <div className="glassy-modal" onClick={(e) => e.stopPropagation()}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="fw-semibold ">Schedule Online Class</div>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div className="small text-muted mb-3">
          {context.name} • {context.email} • {context.className}
          {context.section ? `-${context.section}` : ""}
        </div>

        <Form onSubmit={submit} className="grid gap-2">
          <Form.Group>
            <Form.Label>Title<span className="text-danger">*</span></Form.Label>
            <Form.Control
              value={form.title}
              onChange={(e) => change("title", e.target.value)}
              placeholder="Algebra – Quadratic Equations"
              required
            />
          </Form.Group>

          <div className="grid-2 gap-2">
            <Form.Group>
              <Form.Label>Subject</Form.Label>
              <Form.Control
                value={form.subject}
                onChange={(e) => change("subject", e.target.value)}
                placeholder="Physics"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Platform</Form.Label>
              <Form.Select
                value={form.platform}
                onChange={(e) => change("platform", e.target.value)}
              >
                <option value="meet">Google Meet</option>
                <option value="zoom">Zoom</option>
                <option value="teams">Microsoft Teams</option>
                <option value="other">Other</option>
              </Form.Select>
            </Form.Group>
          </div>

          <div className="grid-2 gap-2">
            <Form.Group>
              <Form.Label>Start at<span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="datetime-local"
                value={form.startAt}
                onChange={(e) => change("startAt", e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>End at</Form.Label>
              <Form.Control
                type="datetime-local"
                value={form.endAt}
                onChange={(e) => change("endAt", e.target.value)}
              />
            </Form.Group>
          </div>

          <div className="grid-2 gap-2">
            <Form.Group>
              <Form.Label>Meeting link</Form.Label>
              <Form.Control
                type="url"
                value={form.meetingURL}
                onChange={(e) => change("meetingURL", e.target.value)}
                placeholder="https://meet.google.com/..."
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Duration (min)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={form.durationMin}
                onChange={(e) => change("durationMin", e.target.value)}
                placeholder="45"
              />
            </Form.Group>
          </div>

          <Form.Group>
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              style={{ height: 90 }}
              value={form.notes}
              onChange={(e) => change("notes", e.target.value)}
              placeholder="Short description, prerequisites, etc."
            />
          </Form.Group>

          <div className="d-flex justify-content-end gap-2 mt-2">
            <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : initial ? "Update" : "Save"}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}

/* ------------ main ------------ */
export default function TeacherOnlineClasses() {
  const { user, profile } = useAuth();
  const schoolId = profile?.schoolId || null;
  const teacherIdFromProfile = profile?.teacherId || null;

  const [teacher, setTeacher] = useState(null);
  const [loadingTeacher, setLoadingTeacher] = useState(true);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // UX filters
  const [qText, setQText] = useState("");
  const [upcomingOnly, setUpcomingOnly] = useState(false);
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // all | live | upcoming | ended

  // modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const teacherEmail = lower(teacher?.email || user?.email || "");
  const teacherName = teacher?.name || profile?.displayName || (user?.email || "").split("@")[0] || "Teacher";
 // classes this teacher owns
const teacherClasses = useMemo(
  () =>
    Array.isArray(teacher?.classIds) && teacher.classIds.length
      ? teacher.classIds.filter(Boolean)
      : (teacher?.className ? [teacher.className] : []),
  [teacher]
);

// sections this teacher owns (uppercased)
const parseSections = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String).map(s=>s.trim()).filter(Boolean).map(s=>s.toUpperCase());
  return String(raw).split(/[,\s/|]+/g).map(s=>s.trim()).filter(Boolean).map(s=>s.toUpperCase());
};
const teacherSectionsU = useMemo(
  () => parseSections(teacher?.sectionIds ?? teacher?.sections ?? teacher?.section),
  [teacher]
);

// selection (active class + section)
const [activeClass, setActiveClass] = useState("");
const [activeSectionU, setActiveSectionU] = useState(""); // "" = all sections

useEffect(() => {
  if (!teacherClasses.length) { setActiveClass(""); return; }
  setActiveClass(prev => (teacherClasses.includes(prev) ? prev : teacherClasses[0]));
}, [teacherClasses]);

useEffect(() => {
  // if currently selected section no longer available, reset to ""
  setActiveSectionU(prev => (prev && !teacherSectionsU.includes(prev) ? "" : prev));
}, [teacherSectionsU]);

// convenience flags
const hasAnySectionMapping = teacherSectionsU.length > 0;

const isInactive = useMemo(
  () => String(teacher?.status || "").toLowerCase() === "inactive",
  [teacher]
);
  /* Resolve teacher doc (by teacherId; fallback email) */
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!schoolId || !user?.uid) {
        setTeacher(null);
        setLoadingTeacher(false);
        return;
      }
      setLoadingTeacher(true);
      try {
        if (teacherIdFromProfile) {
          const snap = await getDoc(doc(db, "schools", schoolId, "teachers", teacherIdFromProfile));
          if (!cancelled && snap.exists()) {
            setTeacher({ id: snap.id, ...(snap.data() || {}) });
            setLoadingTeacher(false);
            return;
          }
        }
        const email = lower(user?.email || "");
        if (email) {
          const qy = query(
            collection(db, "schools", schoolId, "teachers"),
            where("email", "==", email),
            limit(1)
          );
          const qs = await getDocs(qy);
          if (!cancelled && !qs.empty) {
            const d = qs.docs[0];
            setTeacher({ id: d.id, ...(d.data() || {}) });
            setLoadingTeacher(false);
            return;
          }
        }
        if (!cancelled) {
          setTeacher(null);
          setLoadingTeacher(false);
        }
      } catch {
        if (!cancelled) {
          setTeacher(null);
          setLoadingTeacher(false);
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [schoolId, teacherIdFromProfile, user?.email, user?.uid]);

const missingClass = !activeClass; // sections are optional now

  /* Subscribe to classes (hosted/created and targeted to class/section or all sections) */
useEffect(() => {
  if (!schoolId || (!teacher?.id && !teacherEmail && !user?.uid)) {
    setRows([]); setLoading(false);
    return;
  }
  setLoading(true);
  const colRef = collection(db, "schools", schoolId, "onlineClasses");
  const unsubs = [];

  const mergeSnap = (snap) => {
    setRows((prev) => {
      const map = new Map(prev.map((x) => [x.id, x]));
      snap.docs.forEach((d) => {
        const data = d.data() || {};
        map.set(d.id, {
          id: d.id,
          title: data.title || "—",
          subject: data.subject || "",
          className: data.className || "",
          section: data.section ?? "",
          startAt: toDate(data.startAt),
          endAt: toDate(data.endAt),
          durationMin: data.durationMin || null,
          platform: data.platform || "meet",
          meetingURL: data.meetingURL || "",
          notes: data.notes || "",
          status: data.status || "active",
          createdAt: toDate(data.createdAt),
          hostId: data.hostId || "",
          hostEmail: lower(data.hostEmail || ""),
        });
      });
      return Array.from(map.values()).sort(
        (a, b) => (b.startAt?.getTime?.() || 0) - (a.startAt?.getTime?.() || 0)
      );
    });
    setLoading(false);
  };

  // ownership/created-by taps (unchanged)
  if (teacher?.id) {
    unsubs.push(onSnapshot(
      query(colRef, where("hostId", "==", teacher.id), orderBy("startAt", "desc")),
      mergeSnap, () => setLoading(false)
    ));
  }
  if (teacherEmail) {
    unsubs.push(onSnapshot(
      query(colRef, where("hostEmail", "==", teacherEmail), orderBy("startAt", "desc")),
      mergeSnap, () => setLoading(false)
    ));
  }
  if (user?.uid) {
    unsubs.push(onSnapshot(
      query(colRef, where("createdBy.uid", "==", user.uid), orderBy("startAt", "desc")),
      mergeSnap, () => setLoading(false)
    ));
  }

  // classes targeted to the teacher's active class + section(s)
  if (activeClass) {
    // case 1: specific selected section -> subscribe to that section AND to "all sections" rows (section == "")
    if (activeSectionU) {
      unsubs.push(onSnapshot(
        query(colRef, where("className", "==", activeClass), where("section", "==", activeSectionU), orderBy("startAt", "desc")),
        mergeSnap, () => setLoading(false)
      ));
      unsubs.push(onSnapshot(
        query(colRef, where("className", "==", activeClass), where("section", "==", ""), orderBy("startAt", "desc")),
        mergeSnap, () => setLoading(false)
      ));
    } else {
      // case 2: All sections -> subscribe to each of my sections + "" (up to 10 sections; Firestore "in" has limits)
      const secList = teacherSectionsU.length ? teacherSectionsU : [""];
      const uniq = new Set([...secList, ""]);
      uniq.forEach(sec => {
        unsubs.push(onSnapshot(
          query(colRef, where("className", "==", activeClass), where("section", "==", sec), orderBy("startAt", "desc")),
          mergeSnap, () => setLoading(false)
        ));
      });
    }
  }

  return () => unsubs.forEach(u => u && u());
}, [schoolId, teacher?.id, teacherEmail, user?.uid, activeClass, activeSectionU, teacherSectionsU.join("|")]);


  /* Subject list for filter */
  const subjects = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.subject && s.add(r.subject));
    return ["all", ...Array.from(s).sort((a, b) => a.localeCompare(b))];
  }, [rows]);

  /* Filter logic */
const filtered = useMemo(() => {
  const q = qText.trim().toLowerCase();
  return rows.filter((r) => {
    // class match
    if (activeClass && r.className !== activeClass) return false;

    // section match
    if (activeSectionU) {
      // viewing a specific section: show that section + allow rows targeted to all sections ("")
      if (r.section && r.section !== activeSectionU) return false;
    } else {
      // viewing "All Sections": if teacher has sections => show any of my sections or "" (all)
      if (teacherSectionsU.length) {
        const rSec = String(r.section || "").toUpperCase();
        if (!(rSec === "" || teacherSectionsU.includes(rSec))) return false;
      }
      // if no section mapping at all, accept everything for the class
    }

    if (subjectFilter !== "all" && r.subject !== subjectFilter) return false;
    if (statusFilter === "live" && !isLive(r)) return false;
    if (statusFilter === "upcoming" && !isUpcoming(r)) return false;
    if (statusFilter === "ended" && !isEnded(r)) return false;
    if (upcomingOnly && !isUpcoming(r) && !isLive(r)) return false;
    if (q) {
      const hay = [r.title, r.subject, r.meetingURL, r.notes, r.className, r.section]
        .join(" ")
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}, [rows, qText, upcomingOnly, subjectFilter, statusFilter, activeClass, activeSectionU, teacherSectionsU]);


  /* Create/Update */
  const save = async (payload) => {
    if (!schoolId || !teacher) return;
    const base = {
      title: payload.title,
      subject: payload.subject,
      className: activeClass,
      section: activeSectionU || "",
      startAt: payload.startAt || null,
      endAt: payload.endAt || null,
      durationMin: payload.durationMin || null,
      platform: payload.platform || "meet",
      meetingURL: payload.meetingURL || "",
      hostId: teacher.id || "",
      host: teacherName || "",
      hostEmail: teacherEmail || "",
      notes: payload.notes || "",
      status: payload.status || "active",
      updatedAt: serverTimestamp(),
    };

    if (editing) {
      await updateDoc(doc(db, "schools", schoolId, "onlineClasses", editing.id), base);
      Swal.fire({ icon: "success", title: "Updated", timer: 900, showConfirmButton: false });
    } else {
      await addDoc(collection(db, "schools", schoolId, "onlineClasses"), {
        ...base,
        createdAt: serverTimestamp(),
        createdBy: { uid: user?.uid || "", role: "teacher" },
      });
      Swal.fire({ icon: "success", title: "Scheduled", timer: 900, showConfirmButton: false });
    }
  };

  const removeRow = async (row) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Delete online class?",
      text: `Remove "${row.title}" (${row.className}${row.section ? "-" + row.section : ""}).`,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (!res.isConfirmed) return;
    await deleteDoc(doc(db, "schools", schoolId, "onlineClasses", row.id));
    Swal.fire({ icon: "success", title: "Deleted", timer: 900, showConfirmButton: false });
  };

  /* --------- UI --------- */
  if (loadingTeacher) {
    
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-5 text-center text-muted">
          <Spinner animation="border" size="sm" /> Loading your profile…
        </Card.Body>
      </Card>
    );
  }
if (!loadingTeacher && teacher && isInactive) {
  return (
    <Card className="border-0 shadow-sm">
      <Card.Body className="py-4">
        <div className="d-flex align-items-center gap-2 mb-2">
          <i className="bi bi-lock-fill text-danger fs-5" />
          <div className="fw-semibold">Access restricted</div>
        </div>
        <div className="text-muted">
          Your teacher account is marked as <b>Inactive</b>. Please contact the administrator to regain access.
        </div>
      </Card.Body>
    </Card>
  );
}
  if (!teacher || missingClass) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-4">
          <div className="d-flex align-items-center gap-2 mb-2">
            <i className="bi bi-exclamation-triangle text-warning fs-5" />
            <div className="fw-semibold">Your profile doesn’t have a class/section assigned.</div>
          </div>
          <div className="text-muted">
            Ask the school admin to add <b>className</b> and <b>section</b> on your teacher record.
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Container fluid className="p-0 teacher-oc">
      {/* Header */}
      <Row className="mb-3 g-2 align-items-center">
        <Col>
          <div className="small">
           <h5 className="mb-0">Online Classes</h5>
<div className="small mb-1">{teacherName}</div>

<div className="d-flex flex-wrap gap-2">
  <Form.Select
    size="sm"
    style={{ width: 180 }}
    value={activeClass}
    onChange={(e)=>setActiveClass(e.target.value)}
  >
    {teacherClasses.length === 0 ? <option value="">No class</option> : null}
    {teacherClasses.map(c => <option key={c} value={c}>{`Class ${c}`}</option>)}
  </Form.Select>

  {/* Section selector (optional). "" means: All my sections */}
  <Form.Select
    size="sm"
    style={{ width: 180 }}
    value={activeSectionU}
    onChange={(e)=>setActiveSectionU(e.target.value)}
  >
    <option value="">All Sections</option>
    {teacherSectionsU.map(s => <option key={s} value={s}>{`Section ${s}`}</option>)}
  </Form.Select>
</div>

          </div>
        </Col>
        <Col className="text-end ">
          <Button className="btn-accent" onClick={() => { setEditing(null); setShowModal(true); }}>
            <i className="bi bi-plus-lg me-1 " /> Schedule Class
          </Button>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="filter-card">
        <Row className="g-2 align-items-center">
          <Col xl={5} lg={6}>
            <InputGroup>
              <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
              <Form.Control
                placeholder="Search title / subject / notes"
                value={qText}
                onChange={(e) => setQText(e.target.value)}
              />
              {qText && (
                <Button variant="outline-secondary" onClick={() => setQText("")}>Clear</Button>
              )}
            </InputGroup>
          </Col>
          <Col lg={3} md={6}>
            <Form.Select
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
            >
              {subjects.map((s) => (
                <option key={s} value={s}>{s === "all" ? "All subjects" : s}</option>
              ))}
            </Form.Select>
          </Col>
          <Col lg={3} md={6}>
            <Form.Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="live">Live</option>
              <option value="upcoming">Upcoming</option>
              <option value="ended">Ended</option>
            </Form.Select>
          </Col>
          <Col lg={3} className="d-flex justify-content-lg-end align-items-center">
            <Form.Check
              type="switch"
              id="upcoming-only"
              className="me-2"
              checked={upcomingOnly}
              onChange={(e) => setUpcomingOnly(e.target.checked)}
            />
            <label htmlFor="upcoming-only" className="small m-0">Upcoming only</label>
            <Badge className="ms-3 border">
              {filtered.length} / {rows.length}
            </Badge>
          </Col>
        </Row>
      </Card>

      {/* Grid cards (scrollable container) */}
      {loading ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="py-5 text-center">
            <Spinner animation="border" size="sm" /> Loading…
          </Card.Body>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="py-5 text-center text-muted">No online classes found</Card.Body>
        </Card>
      ) : (
        <div className="oc-scroll" role="region" aria-label="Online classes list">
          <div className="oc-grid">
            {filtered.map((c) => {
              const live = isLive(c);
              const upcoming = isUpcoming(c);
              const ended = isEnded(c);

              let hostDomain = "";
              try { hostDomain = c.meetingURL ? new URL(c.meetingURL).hostname : ""; } catch {}

              return (
                <div className="oc-card" key={c.id}>
                  <div className="oc-card__left">
                    <div className="bolt" title={c.platform?.toUpperCase?.() || "Meet"}>
                      <i className="bi bi-lightning-charge" />
                    </div>
                  </div>

                  <div className="oc-card__body">
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      <div className="title" title={c.title}>{c.title}</div>
                      <div>
                        {live && <span className="pill pill-live text-muted">Live</span>}
                        {!live && upcoming && <span className="pill pill-upcoming text-muted">Upcoming</span>}
                        {!live && !upcoming && <span className="pill pill-ended text-muted">Ended</span>}
                      </div>
                    </div>

                    <div className="kv"><span>Subject:</span> <b>{c.subject || "—"}</b></div>
                    <div className="kv"><span>Teacher:</span> <b>{teacherName}</b></div>
                    <div className="kv">
                      <span>Time:</span>{" "}
                      <b>{fmt(c.startAt)}{c.endAt ? `  –  ${fmt(c.endAt)}` : ""}</b>
                    </div>
                    <div className="mt-2">
                      <span className="chip">{[c.className, c.section].filter(Boolean).join(" ")}</span>
                      {hostDomain ? <span className="chip ms-2">{hostDomain}</span> : null}
                    </div>
                  </div>

                  <div className="oc-card__actions">
                    <Button
                      size="sm"
                      className="join-btn"
                      disabled={!c.meetingURL || ended}
                      onClick={() => c.meetingURL && window.open(c.meetingURL, "_blank")}
                      title={ended ? "Class has ended" : (c.meetingURL ? "Join meeting" : "No meeting link")}
                    >
                      <i className="bi bi-camera-video me-1" />
                      {live ? "Join now" : ended ? "Ended" : "Open link"}
                    </Button>
                    <div className="d-flex gap-2 mt-2">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        title="Edit"
                        onClick={() => { setEditing(c); setShowModal(true); }}
                      >
                        <i className="bi bi-pencil" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        title="Delete"
                        onClick={() => removeRow(c)}
                      >
                        <i className="bi bi-trash" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upsert modal */}
      <UpsertModal
        show={showModal}
        onClose={() => { setShowModal(false); setEditing(null); }}
        initial={editing}
        onSave={save}
        context={{ name: teacherName, email: teacherEmail, className: activeClass, section: activeSectionU }}
      />

      {/* Theme-aware styles (+ scroll container) */}
      <style>{`
        .teacher-oc { position: relative; }

        /* Filter bar (glass + dotted bg) */
        .filter-card{
          border-radius: 16px;
          padding: 12px 14px;
          margin-bottom: 12px;
          border: 1px solid var(--border, rgba(0,0,0,.12));
          background:
            radial-gradient(1px 1px at 12px 16px, color-mix(in oklab, var(--text, #222) 8%, transparent), transparent 70%) 0 0/14px 16px,
            color-mix(in oklab, var(--surface, #fff) 95%, var(--accent-10, transparent));
          backdrop-filter: blur(6px);
        }

        /* Scroll container: keeps header/filters static while list scrolls */
        .oc-scroll{
          max-height: calc(100vh - 230px); /* header + filters + breathing space */
          overflow: auto;
          padding-right: 4px;
          scrollbar-gutter: stable both-edges;
          border-radius: 14px;
        }
        @media (max-width: 768px){
          .oc-scroll{ max-height: unset; overflow: visible; } /* use page scroll on mobile */
        }
        /* Pretty scrollbar (webkit) */
        .oc-scroll::-webkit-scrollbar { width: 10px; }
        .oc-scroll::-webkit-scrollbar-thumb {
          background: color-mix(in oklab, var(--brand-accent, #6a8cff) 35%, transparent);
          border-radius: 999px;
        }
        .oc-scroll::-webkit-scrollbar-track {
          background: color-mix(in oklab, var(--surface, #fff) 96%, transparent);
          border-radius: 999px;
        }

        /* Grid */
        .oc-grid{
          display:grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 16px;
        }

        /* Card */
        .oc-card{
          display:grid;
          grid-template-columns: 64px 1fr auto;
          gap: 14px;
          border-radius: 18px;
          border: 1px solid var(--border, rgba(0,0,0,.12));
          background:
            radial-gradient(1px 1px at 18px 18px, color-mix(in oklab, var(--text, #222) 8%, transparent), transparent 70%) 0 0/16px 16px,
            color-mix(in oklab, var(--surface, #fff) 92%, var(--accent-10, transparent));
          padding: 14px;
          position: relative;
          box-shadow: 0 8px 28px color-mix(in oklab, var(--brand-accent, #6a8cff) 10%, transparent);
          transition: transform .12s ease, box-shadow .2s ease, background .2s ease, border-color .2s ease;
        }
        .oc-card:hover{
          transform: translateY(-2px);
          box-shadow: 0 10px 34px color-mix(in oklab, var(--brand-accent, #6a8cff) 20%, transparent);
        }

        .oc-card__left{
          display:flex; align-items:center; justify-content:center;
        }
        .bolt{
          width:52px; height:52px; border-radius:14px;
          display:flex; align-items:center; justify-content:center;
          border:1px solid color-mix(in oklab, var(--brand-accent, #6a8cff) 55%, var(--border, rgba(0,0,0,.12)));
          background: color-mix(in oklab, var(--brand-accent, #6a8cff) 12%, transparent);
          color: var(--brand-accent, #6a8cff);
          font-size: 22px;
        }

        .oc-card__body .title{
          font-weight: 700;
          letter-spacing: .2px;
          max-width: 420px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .oc-card__body .kv{ font-size: .925rem; color: var(--muted, #6c757d); }
        .oc-card__body .kv span{ color: var(--muted, #6c757d); margin-right: 6px; }
        .chip{
          display:inline-block; padding:4px 10px; border-radius: 999px;
          background: color-mix(in oklab, var(--brand-accent, #6a8cff) 12%, transparent);
          border: 1px solid color-mix(in oklab, var(--brand-accent, #6a8cff) 40%, var(--border, rgba(0,0,0,.12)));
          color: var(--brand-accent, #6a8cff); font-weight: 600; font-size: .82rem;
        }

        .pill{
          display:inline-block; padding:4px 10px; border-radius: 999px; font-size: .75rem; font-weight: 700;
        }
        .pill-live{
          background: color-mix(in oklab, crimson 18%, transparent);
          color: #ffccd3;
          border:1px solid color-mix(in oklab, crimson 40%, var(--border, rgba(0,0,0,.12)));
        }
        .pill-upcoming{
          background: color-mix(in oklab, #00bb88 14%, transparent);
          color: #b8ffe9;
          border:1px solid color-mix(in oklab, #00bb88 35%, var(--border, rgba(0,0,0,.12)));
        }
        .pill-ended{
          background: color-mix(in oklab, gray 14%, transparent);
          color: #e2e2e2;
          border:1px solid color-mix(in oklab, gray 35%, var(--border, rgba(0,0,0,.12)));
        }

        .oc-card__actions{
          display:flex; flex-direction:column; align-items:flex-end; justify-content:center; gap:6px;
        }
        .join-btn{
          --g: color-mix(in oklab, var(--brand-accent, #6a8cff) 60%, royalblue);
          background: radial-gradient(150% 200% at 0% 0%, var(--g), color-mix(in oklab, var(--g) 25%, transparent));
          border:1px solid color-mix(in oklab, var(--g) 55%, var(--border, rgba(0,0,0,.12)));
          color: white;
          box-shadow: 0 6px 20px color-mix(in oklab, var(--g) 40%, transparent);
        }
        .join-btn:disabled{
          opacity:.65; cursor:not-allowed; filter: grayscale(.25);
        }

        /* Glassy modal */
        .glassy-backdrop{
          position: fixed; inset:0; z-index: 1100;
          background: color-mix(in oklab, #000 35%, transparent);
          display:flex; align-items:center; justify-content:center;
          backdrop-filter: blur(2px);
        }
        .glassy-modal{
          width: min(760px, 95vw);
          border-radius: 16px; padding:16px;
          border:1px solid var(--border, rgba(255,255,255,.14));
          background: color-mix(in oklab, var(--surface, #fff) 96%, var(--accent-10, transparent));
          box-shadow: 0 16px 44px rgba(0,0,0,.25);
        }
        .grid{ display:grid; }
        .grid-2{ display:grid; grid-template-columns: 1fr 1fr; }
        .gap-2{ gap: .75rem; }
        @media (max-width: 768px){
          .grid-2{ grid-template-columns: 1fr; }
        }
      `}</style>
    </Container>
  );
}
